import { useMemo, useState } from "react";

type AllQual = "-all" | "~all" | "?all";

// Common providers → the include: mechanism they require.
const PROVIDERS: { id: string; label: string; include: string }[] = [
  { id: "google", label: "Google Workspace / Gmail", include: "_spf.google.com" },
  { id: "microsoft", label: "Microsoft 365 / Outlook", include: "spf.protection.outlook.com" },
  { id: "mailchimp", label: "Mailchimp", include: "servers.mcsv.net" },
  { id: "sendgrid", label: "SendGrid", include: "sendgrid.net" },
  { id: "mailgun", label: "Mailgun", include: "mailgun.org" },
  { id: "amazonses", label: "Amazon SES", include: "amazonses.com" },
  { id: "zoho", label: "Zoho Mail", include: "zoho.com" },
  { id: "fastmail", label: "Fastmail", include: "spf.messagingengine.com" },
];

export function SpfGenerator() {
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [ip4, setIp4] = useState("");
  const [ip6, setIp6] = useState("");
  const [extra, setExtra] = useState("");
  const [allQual, setAllQual] = useState<AllQual>("~all");
  const [copied, setCopied] = useState(false);

  const includes = PROVIDERS.filter((p) => selected[p.id]);

  const record = useMemo(() => {
    const mech: string[] = ["v=spf1"];
    // Pair each field with its mechanism prefix explicitly. Deriving the prefix
    // by comparing `list === ip6` compared string VALUES, so identical text in
    // both fields (or both empty) mislabeled the ip4 entries as ip6.
    for (const [prefix, raw] of [["ip4", ip4], ["ip6", ip6]] as const) {
      for (const v of splitList(raw)) {
        mech.push(`${prefix}:${v}`);
      }
    }
    for (const p of includes) mech.push(`include:${p.include}`);
    for (const e of splitList(extra)) {
      mech.push(e.startsWith("include:") || e.includes(":") ? e : `include:${e}`);
    }
    mech.push(allQual);
    return mech.join(" ");
  }, [ip4, ip6, extra, includes, allQual]);

  // SPF has a hard 10-DNS-lookup limit. include: and a/mx each cost one at the
  // TOP level — but an include: can itself contain includes, so the real total
  // is only ever >= this number. We show it as a minimum ("≈") rather than
  // implying we've resolved and counted the full nested chain.
  const lookupCount = includes.length + splitList(extra).filter((e) => /^(include:|a$|mx$|a:|mx:|exists:|redirect=)/.test(e)).length;
  const overLimit = lookupCount > 10;

  const copy = () => {
    navigator.clipboard?.writeText(record).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <main className="console">
      <div className="input-row">
        <span className="input-row-label">SPF</span>
        <span className="gen-intro">Build an SPF record, then publish it as a TXT record at your domain apex (<code>@</code>).</span>
      </div>

      <div className="gen-grid">
        <section className="gen-form">
          <fieldset className="gen-field gen-providers">
            <legend className="gen-label">Email providers you send through</legend>
            {PROVIDERS.map((p) => (
              <label key={p.id} className="gen-check">
                <input
                  type="checkbox"
                  checked={!!selected[p.id]}
                  onChange={(e) => setSelected((s) => ({ ...s, [p.id]: e.target.checked }))}
                />
                <span>{p.label} <code>{p.include}</code></span>
              </label>
            ))}
          </fieldset>

          <label className="gen-field">
            <span className="gen-label">Own IPv4 addresses</span>
            <input type="text" value={ip4} onChange={(e) => setIp4(e.target.value)} placeholder="203.0.113.5, 198.51.100.0/24" spellCheck={false} autoCapitalize="off" autoCorrect="off" />
            <span className="gen-hint">Comma-separate. Single IPs or CIDR ranges.</span>
          </label>

          <label className="gen-field">
            <span className="gen-label">Own IPv6 addresses</span>
            <input type="text" value={ip6} onChange={(e) => setIp6(e.target.value)} placeholder="2001:db8::1" spellCheck={false} autoCapitalize="off" autoCorrect="off" />
          </label>

          <label className="gen-field">
            <span className="gen-label">Other includes</span>
            <input type="text" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="spf.example.net" spellCheck={false} autoCapitalize="off" autoCorrect="off" />
            <span className="gen-hint">Any provider not listed above. Comma-separate.</span>
          </label>

          <label className="gen-field">
            <span className="gen-label">Policy for everything else</span>
            <select value={allQual} onChange={(e) => setAllQual(e.target.value as AllQual)} aria-label="SPF all qualifier">
              <option value="~all">~all — softfail (recommended)</option>
              <option value="-all">-all — hardfail / reject</option>
              <option value="?all">?all — neutral</option>
            </select>
            <span className="gen-hint"><strong>~all</strong> is the safe default; move to <strong>-all</strong> once you're confident the record is complete.</span>
          </label>
        </section>

        <section className="gen-output">
          <div className="gen-out-head">
            <span className="gen-out-label">Your SPF record</span>
            <button type="button" className="gen-copy" onClick={copy}>{copied ? "Copied ✓" : "Copy"}</button>
          </div>
          <pre className="gen-record"><code>{record}</code></pre>

          <p className={`gen-lookups ${overLimit ? "gen-over" : ""}`}>
            ≈{lookupCount} of 10 DNS lookups used
            {overLimit
              ? " — over the limit! SPF will PermError. Remove some includes or flatten them."
              : " (at least — each include: may nest more inside it)"}
          </p>

          <dl className="gen-dns">
            <div><dt>Type</dt><dd>TXT</dd></div>
            <div><dt>Name / Host</dt><dd><code>@</code> (the apex)</dd></div>
            <div><dt>Value</dt><dd>the record above</dd></div>
            <div><dt>TTL</dt><dd>3600 (1 hour) is fine</dd></div>
          </dl>

          <p className="gen-warn-note">Only ever publish <strong>one</strong> SPF record per domain. Two <code>v=spf1</code> records is a permanent error.</p>

          <p className="whois-cross">
            After publishing, verify it:{" "}
            <a href="/spf-check">SPF check</a> · learn more in the{" "}
            <a href="/glossary/spf">SPF glossary</a>.
          </p>
        </section>
      </div>

    </main>
  );
}

function splitList(raw: string): string[] {
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}
